import { EventEmitter } from './EventEmitter';

import { IPlatformAppEventBase, IPlatformEventBase } from './events';

export enum PlatformAppEvent {
  ApplicationInit = 'ApplicationInit',
  ApplicationExecute = 'ApplicationExecute',
  ApplicationRemoved = 'ApplicationRemoved',
  ApplicationLoad = 'ApplicationLoad',
  ApplicationApiInit = 'ApplicationApiInit',
  CustomEvent = 'CustomEvent',
  HostEvent = 'HostEvent',

  EditorReady = 'EditorReady',
}

export type IPlatformAppInitEvent = IPlatformAppEventBase<
  PlatformAppEvent.ApplicationInit,
  {},
  {}
>;

export type IPlatformAppCustomEvent = IPlatformAppEventBase<
  PlatformAppEvent.CustomEvent,
  {
    type: string;
  } & Record<string, string>,
  {}
>;

export type IPlatformAppHostEvent = IPlatformAppEventBase<
  PlatformAppEvent.HostEvent,
  {
    type: string;
  } & Record<string, string>,
  {}
>;

export type IPlatformAppExecuteEvent = IPlatformAppEventBase<
  PlatformAppEvent.ApplicationExecute,
  {
    url: string;
    appDefinitionId: string;
  },
  {
    bi: {
      appDefId: string;
      appType: string;
      bundleUrl: string;
    };
  }
>;

export type IPlatformEditorReadyEvent = IPlatformEventBase<
  PlatformAppEvent.EditorReady,
  {},
  {}
>;

export type IPlatformApplicationRemovedEvent = IPlatformAppEventBase<
  PlatformAppEvent.ApplicationRemoved,
  {},
  {
    bi: {
      appDefId: string;
    };
  }
>;

export type IPlatformAppLoadEvent = IPlatformAppEventBase<
  PlatformAppEvent.ApplicationLoad,
  {
    url: string;
    appDefinitionId: string;
  },
  {
    bi: {
      appDefId: string;
      appType: string;
      bundleUrl: string;
    };
  }
>;

export type IPlatformApplicationApiInitEvent = IPlatformAppEventBase<
  PlatformAppEvent.ApplicationApiInit,
  {},
  {
    bi: {
      appDefId: string;
      scope: 'private' | 'public';
    };
  }
>;

export type IPlatformAppEvent =
  | IPlatformAppInitEvent
  | IPlatformAppCustomEvent
  | IPlatformAppHostEvent
  | IPlatformAppExecuteEvent
  | IPlatformAppLoadEvent
  | IPlatformApplicationRemovedEvent
  | IPlatformApplicationApiInitEvent;

class PlatformAppEventsFactories {
  public createApplicationRemovedEvent(
    appDefinitionId: string,
  ): IPlatformApplicationRemovedEvent {
    return {
      type: PlatformAppEvent.ApplicationRemoved,
      payload: {
        appDefinitionId,
      },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents.PLATFORM_WORKER_SPAWN,
          appDefId: appDefinitionId,
        },
      },
    };
  }

  public createApplicationLoadEvent(
    app: {
      appDefinitionId: string;
      appType: string;
    },
    url: string,
  ): IPlatformAppLoadEvent {
    return {
      type: PlatformAppEvent.ApplicationLoad,
      payload: {
        appDefinitionId: app.appDefinitionId,
        url,
      },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents
            .PLATFORM_WORKER_APP_BUNDLE_LOAD,
          appDefId: app.appDefinitionId,
          appType: app.appType,
          bundleUrl: url,
        },
      },
    };
  }

  public createApplicationExecuteEvent(
    app: {
      appDefinitionId: string;
      appType: string;
    },
    url: string,
  ): IPlatformAppExecuteEvent {
    return {
      type: PlatformAppEvent.ApplicationExecute,
      payload: {
        appDefinitionId: app.appDefinitionId,
        url,
      },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents
            .PLATFORM_WORKER_APP_BUNDLE_EXECUTE,
          appDefId: app.appDefinitionId,
          appType: app.appType,
          bundleUrl: url,
        },
      },
    };
  }

  public createApplicationApiInitEvent(
    appDefinitionId: string,
    scope: 'private' | 'public',
  ): IPlatformApplicationApiInitEvent {
    return {
      type: PlatformAppEvent.ApplicationApiInit,
      payload: { appDefinitionId },
      meta: {
        bi: {
          evid: PlatformAppEventEmitter.biEvents.PLATFORM_APP_API_INIT,
          appDefId: appDefinitionId,
          scope,
        },
      },
    };
  }
}

export class PlatformAppEventEmitter extends EventEmitter<IPlatformAppEvent> {
  public factories = new PlatformAppEventsFactories();
}
